import React, { useState, useRef, useEffect } from 'react';
import { FiAlertTriangle, FiInfo, FiX, FiCheckCircle, FiPlus, FiMove } from 'react-icons/fi';
import type { MissingResource, ArchitectureSuggestion } from '../../api/dependencies';
import './DependencyDetector.css';

interface DependencyDetectorProps {
  missingResources: MissingResource[];
  suggestions: ArchitectureSuggestion[];
  onAutoCreate: (resources: MissingResource[]) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
  currentNodes?: any[];
}

const DependencyDetector: React.FC<DependencyDetectorProps> = ({
  missingResources,
  suggestions,
  onAutoCreate,
  onClose,
  isLoading = false,
  currentNodes = []
}) => {
  const [selectedResources, setSelectedResources] = useState<Set<number>>(
    new Set(missingResources.map((_, idx) => idx))
  );
  const [isCreating, setIsCreating] = useState(false);

  // Draggable panel state
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const getSeverityIcon = (severity: 'high' | 'medium' | 'low') => {
    switch (severity) {
      case 'high':
        return <FiAlertTriangle className="severity-icon high" />;
      case 'medium':
        return <FiInfo className="severity-icon medium" />;
      case 'low':
        return <FiInfo className="severity-icon low" />;
    }
  };

  const getSeverityClass = (severity: 'high' | 'medium' | 'low') => {
    return `severity-badge ${severity}`;
  };

  const getResourceTypeDisplay = (type: string) => {
    // Format resource type for display
    return type.replace('azurerm_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const toggleResource = (index: number) => {
    const newSelected = new Set(selectedResources);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedResources(newSelected);
  };

  const toggleAll = () => {
    if (selectedResources.size === missingResources.length) {
      setSelectedResources(new Set());
    } else {
      setSelectedResources(new Set(missingResources.map((_, idx) => idx)));
    }
  };

  const handleAutoCreate = async () => {
    const resourcesToCreate = missingResources.filter((_, idx) => selectedResources.has(idx));

    if (resourcesToCreate.length === 0) {
      return;
    }

    setIsCreating(true);
    try {
      await onAutoCreate(resourcesToCreate);
      // Clear selection after successful creation
      setSelectedResources(new Set());
    } catch (error) {
      console.error('Failed to auto-create resources:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuggestionAction = async (suggestion: ArchitectureSuggestion) => {
    // Parse action string to create appropriate resource
    // Actions like "add_security_group" should map to resource types
    const actionToResourceType: Record<string, string> = {
      'add_security_group': 'aws_security_group',
      'add_network_security_group': 'azurerm_network_security_group',
      'add_nsg': 'azurerm_network_security_group',
      'add_firewall': 'google_compute_firewall',
      'add_nat_gateway': 'aws_nat_gateway',
      'add_internet_gateway': 'aws_internet_gateway',
      'add_route_table': 'aws_route_table',
    };

    const resourceType = actionToResourceType[suggestion.action];
    if (!resourceType) {
      console.warn(`Unknown action: ${suggestion.action}`);
      return;
    }

    // Find nodes that would need this resource (to position it near them)
    const sourceNodes: string[] = [];

    // Determine which nodes need this resource based on the resource type
    if (resourceType === 'aws_security_group') {
      // Find all EC2 instances
      const ec2Nodes = currentNodes.filter((n: any) =>
        (n.data?.resourceType || n.data?.type) === 'aws_instance'
      );
      sourceNodes.push(...ec2Nodes.map((n: any) => n.id));
    } else if (resourceType === 'azurerm_network_security_group') {
      // Find all Azure VMs
      const vmNodes = currentNodes.filter((n: any) => {
        const type = n.data?.resourceType || n.data?.type;
        return type === 'azurerm_virtual_machine' ||
               type === 'azurerm_linux_virtual_machine' ||
               type === 'azurerm_windows_virtual_machine';
      });
      sourceNodes.push(...vmNodes.map((n: any) => n.id));
    }

    // Create a MissingResource object from the suggestion
    const missingResource: MissingResource = {
      type: resourceType,
      name: `${resourceType.split('_').pop()}-default`,
      reason: suggestion.message,
      severity: suggestion.severity,
      defaultProperties: {},
      sourceNodes: sourceNodes.length > 0 ? sourceNodes : undefined
    };

    setIsCreating(true);
    try {
      await onAutoCreate([missingResource]);
      // Close the panel after successful creation to allow re-analysis
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Failed to create resource from suggestion:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const hasMissingResources = missingResources.length > 0;
  const hasSuggestions = suggestions.length > 0;

  if (!hasMissingResources && !hasSuggestions) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className="dependency-detector-panel floating"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1000,
      }}
    >
      <div
        className="dependency-detector-header draggable"
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="header-title">
          <FiMove className="drag-icon" size={16} style={{ opacity: 0.5 }} />
          <FiAlertTriangle className="header-icon" />
          <h3>Dependency Detection</h3>
        </div>
        <button
          className="close-btn"
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          title="Close"
        >
          <FiX />
        </button>
      </div>

      <div className="dependency-detector-content">
        {isLoading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Analyzing dependencies...</p>
          </div>
        )}

        {!isLoading && hasMissingResources && (
          <div className="missing-resources-section">
            <div className="section-header">
              <h4>Missing Dependencies ({missingResources.length})</h4>
              {missingResources.length > 1 && (
                <button
                  className="select-all-btn"
                  onClick={toggleAll}
                >
                  {selectedResources.size === missingResources.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            <div className="missing-resources-list">
              {missingResources.map((resource, index) => (
                <div
                  key={index}
                  className={`missing-resource-item ${selectedResources.has(index) ? 'selected' : ''}`}
                  onClick={() => toggleResource(index)}
                >
                  <div className="resource-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedResources.has(index)}
                      onChange={() => toggleResource(index)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="resource-content">
                    <div className="resource-header">
                      {getSeverityIcon(resource.severity)}
                      <span className="resource-type">
                        {getResourceTypeDisplay(resource.type)}
                      </span>
                      <span className={getSeverityClass(resource.severity)}>
                        {resource.severity}
                      </span>
                    </div>
                    <div className="resource-details">
                      <p className="resource-name">{resource.name}</p>
                      <p className="resource-reason">{resource.reason}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="actions-bar">
              <button
                className="auto-create-btn"
                onClick={handleAutoCreate}
                disabled={selectedResources.size === 0 || isCreating}
              >
                {isCreating ? (
                  <>
                    <div className="btn-spinner"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Auto-Create Selected ({selectedResources.size})
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {!isLoading && hasSuggestions && (
          <div className="suggestions-section">
            <div className="section-header">
              <h4>Architecture Suggestions ({suggestions.length})</h4>
            </div>

            <div className="suggestions-list">
              {suggestions.map((suggestion, index) => (
                <div key={index} className="suggestion-item">
                  <div className="suggestion-header">
                    {getSeverityIcon(suggestion.severity)}
                    <span className={getSeverityClass(suggestion.severity)}>
                      {suggestion.type.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="suggestion-content">
                    <p className="suggestion-message">{suggestion.message}</p>
                    {suggestion.action && (
                      <button
                        className="suggestion-action-btn"
                        onClick={() => handleSuggestionAction(suggestion)}
                        title={`Click to ${suggestion.action.replace(/_/g, ' ')}`}
                      >
                        <FiPlus className="action-icon" />
                        {suggestion.action.replace(/_/g, ' ')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && !hasMissingResources && !hasSuggestions && (
          <div className="no-issues">
            <FiCheckCircle className="success-icon" />
            <p>No dependency issues detected</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DependencyDetector;
